import { useEffect, useState, useRef } from "react";
import {
  collection,
  query,
  where,
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { db, storage } from "../firebase";
import { getDocs, getDoc } from "firebase/firestore";
import useCurrentUser from "../hooks/useCurrentUser";
import { differenceInDays, parseISO, isToday } from "date-fns";
import { CSVLink } from "react-csv";
import { Download, FileText, FileDown, Plus, Search, Filter } from "lucide-react";

import ContainerForm from "./containertables/ContainerForm";
import ContainerTable from "./containertables/ContainerTable";
// We'll replace StatsHeader with inline StatCards for consistency
import { CheckCircle, Clock, Anchor, AlertCircle } from "lucide-react";

// --- Local Components ---

function StatCard({ label, value, icon: Icon, colorClass, active, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`bg-white p-5 rounded-lg border cursor-pointer transition-all duration-200 ${active
        ? 'border-gold-500 ring-1 ring-gold-500 shadow-md'
        : 'border-slate-200 hover:border-slate-300 shadow-sm'
        }`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{label}</p>
          <h3 className="text-2xl font-bold text-navy-900 mt-2">{value}</h3>
        </div>
        <div className={`p-2.5 rounded-full ${colorClass} bg-opacity-10`}>
          <Icon size={20} className={colorClass.replace("bg-", "text-")} />
        </div>
      </div>
    </div>
  );
}

export default function CustomsDocs() {
  const { clientId } = useCurrentUser();
  const [containers, setContainers] = useState([]);
  const [form, setForm] = useState(initialForm());
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [sort, setSort] = useState({ key: "cutoff", direction: "asc" });
  const [showForm, setShowForm] = useState(false);
  const [searchRef, setSearchRef] = useState("");
  const [debouncedRef, setDebouncedRef] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null); // Filter by status group
  const csvRef = useRef();
  const docTypes = ["PackingList", "Invoice", "BillOfLading"];

  function initialForm() {
    return {
      id: "",
      reference: "",
      origin: "",
      destination: "",
      eta: "",
      cutoff: "",
      dpi: "",
      incoterms: "",
      containerType: "FCL",
      mode: "Sea",
      status: "Booked",
    };
  }

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedRef(searchRef), 300);
    return () => clearTimeout(handler);
  }, [searchRef]);

  useEffect(() => {
    if (!clientId) return;
    const q = query(collection(db, "containers"), where("companyId", "==", clientId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setContainers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );
    return () => unsub();
  }, [clientId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    // Enforce entry limits for new shipments
    if (!form.id) {
      try {
        const subsQ = query(collection(db, "subscriptions"), where("tenantId", "==", clientId), where("status", "==", "active"));
        const subsSnap = await getDocs(subsQ);
        if (!subsSnap.empty) {
          const activeSub = subsSnap.docs[0].data();
          const planSnap = await getDoc(doc(db, "plans", activeSub.planId));
          if (planSnap.exists()) {
            const entryLimit = planSnap.data().entryLimit;
            if (entryLimit !== -1 && containers.length >= entryLimit) {
              alert(`Action blocked by subscription plan. Your current plan (${planSnap.data().name}) limits you to ${entryLimit} shipments. Please contact Super-Admin to upgrade.`);
              return;
            }
          }
        }
      } catch (err) {
        console.error("Error checking subscription limits:", err);
      }
    }

    const id = form.id || crypto.randomUUID();
    if (!form.origin || !form.destination || !form.reference) {
      alert("Please complete Reference, Origin and Destination fields.");
      return;
    }
    const refDoc = doc(db, "containers", id);
    await setDoc(refDoc, { ...form, id, companyId: clientId, createdAt: serverTimestamp() });

    // Don't maximize form, just reset and close
    setForm(initialForm());
    setSelectedId(null);
    setShowForm(false);
  };

  const handleEdit = (data) => {
    setForm(data);
    setSelectedId(data.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to form
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this shipment?")) return;
    await deleteDoc(doc(db, "containers", id));
    setForm(initialForm());
    setSelectedId(null);
    setShowForm(false);
  };

  const handleUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file || !selectedId) return;
    const path = `containers/${clientId}/${selectedId}/${type}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    task.on("state_changed", (snap) => {
      const pct = (snap.bytesTransferred / snap.totalBytes) * 100;
      setUploadProgress((p) => ({ ...p, [type]: pct }));
    });
    task.then(async () => {
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, "containers", selectedId), {
        [`docs.${type}`]: url,
      });
      setUploadProgress((p) => ({ ...p, [type]: 0 }));
    });
  };

  const sortBy = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Filter Logic
  const filteredContainers = containers
    .filter((c) => (c.reference || "").toLowerCase().includes(debouncedRef.toLowerCase()))
    .filter((c) => {
      if (!selectedStatus) return true;
      const s = (c.status || "").toLowerCase();

      if (selectedStatus === "Action") {
        // Show delayed or nearing cutoff
        const isDelayed = s === "delayed" || s === "hold";
        const dueDays = c.cutoff ? differenceInDays(parseISO(c.cutoff), new Date()) : 100;
        const isNearCutoff = dueDays <= 3 && dueDays >= 0;
        return isDelayed || isNearCutoff;
      }
      if (selectedStatus === "In Transit") {
        return ["in transit", "dispatched", "on board"].includes(s);
      }
      if (selectedStatus === "Arrived") {
        return ["arrived", "delivered"].includes(s);
      }
      return true;
    });

  // KPI Data
  const dueSoonCount = containers.filter((c) => {
    const dueDays = c.cutoff ? differenceInDays(parseISO(c.cutoff), new Date()) : 100;
    return dueDays <= 3 && dueDays >= 0;
  }).length;

  const inTransitCount = containers.filter(c => ["in transit", "dispatched", "on board"].includes((c.status || "").toLowerCase())).length;
  const arrivedCount = containers.filter(c => ["arrived", "delivered"].includes((c.status || "").toLowerCase())).length;

  return (
    <div className="pb-12 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-navy-900 font-serif">Shipment Tracking</h1>
          <p className="text-slate-500 mt-1">Monitor active shipments, documentation, and milestones.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setForm(initialForm()); setSelectedId(null); setShowForm(true); }}
            className="bg-navy-900 hover:bg-navy-800 text-white px-5 py-2.5 rounded shadow-sm text-sm font-semibold flex items-center gap-2 transition-colors"
          >
            <Plus size={18} /> New Shipment
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <StatCard
          label="All Active"
          value={containers.length}
          icon={Anchor}
          colorClass="bg-blue-600"
          active={selectedStatus === null}
          onClick={() => setSelectedStatus(null)}
        />
        <StatCard
          label="In Transit"
          value={inTransitCount}
          icon={Clock}
          colorClass="bg-indigo-600"
          active={selectedStatus === "In Transit"}
          onClick={() => setSelectedStatus("In Transit")}
        />
        <StatCard
          label="Arrived / Delivered"
          value={arrivedCount}
          icon={CheckCircle}
          colorClass="bg-emerald-600"
          active={selectedStatus === "Arrived"}
          onClick={() => setSelectedStatus("Arrived")}
        />
        <StatCard
          label="Requires Action"
          value={dueSoonCount}
          icon={AlertCircle}
          colorClass="bg-amber-500"
          active={selectedStatus === "Action"}
          onClick={() => setSelectedStatus("Action")}
        />
      </div>

      {/* Form Drawer / Modal Area */}
      {showForm && (
        <ContainerForm
          form={form}
          handleChange={handleChange}
          handleSave={handleSave}
          handleDelete={handleDelete}
          handleUpload={handleUpload}
          selectedId={selectedId}
          uploadProgress={uploadProgress}
          docTypes={docTypes}
          onClose={() => setShowForm(false)}
        />
      )}

      {/* Main Table Area */}
      <div className="space-y-4">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          {/* Search */}
          <div className="relative w-full sm:w-96">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by Reference, Origin, Destination..."
              value={searchRef}
              onChange={(e) => setSearchRef(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded bg-white text-sm focus:outline-none focus:border-navy-900 focus:ring-1 focus:ring-navy-900"
            />
          </div>

          {/* Exports */}
          <div className="flex gap-2">
            <CSVLink
              data={containers}
              filename="shipments.csv"
              className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-600 px-3 py-2 rounded text-sm font-medium flex items-center gap-2 transition-colors"
              target="_blank"
            >
              <FileDown size={16} /> Export CSV
            </CSVLink>
          </div>
        </div>

        {/* Table */}
        <ContainerTable
          containers={filteredContainers}
          sort={sort}
          sortBy={sortBy}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          badge={{}} // Badge logic moved to table component
        />
      </div>
    </div>
  );
}
